import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { pageTabsSx } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import {
  Box,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import UserManagement from './UserManagement';
import TeamManagement from './TeamManagement';
import JobTagManagement from './JobTagManagement';
import EmailConfiguration from './settings/EmailConfiguration';
import DashboardSettings from './settings/DashboardSettings';
import BrandingSettings from './settings/BrandingSettings';
import AssetViewsSettings from './settings/AssetViewsSettings';
import LocationViewsSettings from './settings/LocationViewsSettings';
import HomeLocationsSettings from './settings/HomeLocationsSettings';
import RolesSettings from './settings/RolesSettings';
import SyncConflicts from './SyncConflicts';
import SharedAccountManagement from './SharedAccountManagement';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { useSiteTerminology } from '../../hooks/useSiteTerminology';
import { locationLabel } from '../../utils/terminology';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

const SETTINGS_TAB_SLUGS = [
  'users',
  'teams',
  'roles',
  'job-tags',
  'email',
  'dashboard',
  'sync-conflicts',
  'branding',
  'asset-views',
  'location-views',
  'home-locations',
  'shared-accounts',
] as const;

const SHARED_ACCOUNTS_TAB_INDEX = SETTINGS_TAB_SLUGS.indexOf('shared-accounts');

const tabSlugToIndex = (slug: string | null, allowSharedAccounts: boolean): number => {
  if (!slug) return 0;
  if (slug === 'locations') return SETTINGS_TAB_SLUGS.indexOf('home-locations');
  const idx = SETTINGS_TAB_SLUGS.indexOf(slug as (typeof SETTINGS_TAB_SLUGS)[number]);
  if (idx < 0) return 0;
  if (idx === SHARED_ACCOUNTS_TAB_INDEX && !allowSharedAccounts) return 0;
  return idx;
};

interface SettingsProps {
  initialTab?: number;
}

const Settings: React.FC<SettingsProps> = ({ initialTab = 0 }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { useSite } = useSiteTerminology();
  const [allowSharedAccounts, setAllowSharedAccounts] = useState(false);
  const [featureFlagsLoaded, setFeatureFlagsLoaded] = useState(false);
  const [value, setValue] = useState(() => {
    const fromUrl = tabSlugToIndex(new URLSearchParams(window.location.search).get('tab'), false);
    if (fromUrl > 0) return fromUrl;
    return initialTab === SHARED_ACCOUNTS_TAB_INDEX ? 0 : initialTab;
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const businessId = localStorage.getItem('businessId');
        if (!token || !businessId) {
          if (!cancelled) setFeatureFlagsLoaded(true);
          return;
        }
        const res = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const enabled =
          res.data?.allow_shared_accounts === 1 || res.data?.allow_shared_accounts === true;
        if (!cancelled) {
          setAllowSharedAccounts(enabled);
          setFeatureFlagsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setAllowSharedAccounts(false);
          setFeatureFlagsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!featureFlagsLoaded) return;
    if (allowSharedAccounts && initialTab === SHARED_ACCOUNTS_TAB_INDEX && !searchParams.get('tab')) {
      setValue(SETTINGS_TAB_SLUGS.indexOf('asset-views'));
    }
  }, [featureFlagsLoaded, allowSharedAccounts, initialTab, searchParams]);

  useEffect(() => {
    if (!featureFlagsLoaded) return;
    if (!allowSharedAccounts && value === SHARED_ACCOUNTS_TAB_INDEX) {
      setValue(0);
      setSearchParams({}, { replace: true });
    }
  }, [featureFlagsLoaded, allowSharedAccounts, value, setSearchParams]);

  useEffect(() => {
    if (!featureFlagsLoaded) return;
    const tab = searchParams.get('tab');
    if (!tab) return;
    const idx = tabSlugToIndex(tab, allowSharedAccounts);
    if (idx !== value) setValue(idx);
  }, [searchParams, featureFlagsLoaded, allowSharedAccounts, value]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    const slug = SETTINGS_TAB_SLUGS[newValue];
    if (newValue === 0) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: slug }, { replace: true });
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader title="Settings" subtitle="Admin" />
      <Paper sx={{ width: '100%', mb: 2, borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="settings tabs"
            variant="fullWidth"
            sx={pageTabsSx}
          >
            <Tab label="Users" {...a11yProps(0)} />
            <Tab label="Teams" {...a11yProps(1)} />
            <Tab label="Roles" {...a11yProps(2)} />
            <Tab label="Tags" {...a11yProps(3)} />
            <Tab label="Email Configuration" {...a11yProps(4)} />
            <Tab label="Dashboard Settings" {...a11yProps(5)} />
            <Tab label="Sync Conflicts" {...a11yProps(6)} />
            <Tab label="Branding" {...a11yProps(7)} />
            <Tab label="Asset Views" {...a11yProps(8)} />
            <Tab label="Location Views" {...a11yProps(9)} />
            <Tab label={locationLabel(useSite, { plural: true })} {...a11yProps(10)} />
            {allowSharedAccounts && <Tab label="Shared Accounts" {...a11yProps(11)} />}
          </Tabs>
      </Paper>

      <TabPanel value={value} index={0}>
        <UserManagement />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <TeamManagement />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <RolesSettings />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <JobTagManagement />
      </TabPanel>
      <TabPanel value={value} index={4}>
        <EmailConfiguration />
      </TabPanel>
      <TabPanel value={value} index={5}>
        <DashboardSettings />
      </TabPanel>
      <TabPanel value={value} index={6}>
        <SyncConflicts />
      </TabPanel>
      <TabPanel value={value} index={7}>
        <BrandingSettings />
      </TabPanel>
      <TabPanel value={value} index={8}>
        <AssetViewsSettings />
      </TabPanel>
      <TabPanel value={value} index={9}>
        <LocationViewsSettings />
      </TabPanel>
      <TabPanel value={value} index={10}>
        <HomeLocationsSettings />
      </TabPanel>
      {allowSharedAccounts && (
        <TabPanel value={value} index={11}>
          <SharedAccountManagement />
        </TabPanel>
      )}
    </Box>
  );
};

export default Settings; 